/**
 * MODELS · OVERVIEW — één rij per model onder de live steps.
 *
 * 16-09 (Jordy): "een overzicht met alle modellen, met per model hoeveel
 * rondes er zijn geweest, hoeveel scheduled posts er nog niet live zijn,
 * hoeveel er al live staan, hoeveel items er in de vault staan en hoeveel er
 * gefaald zijn."
 *
 * Alle getallen komen uit `/api/admin/posting/model-overzicht`, dat dezelfde
 * bronnen gebruikt als de blokken eromheen: de vaultkolom telt precies de
 * rijen van "VAULT DELETE · LIVE" en de foutkolom volgt dezelfde regels als
 * "FAILED POSTS · 24 HOURS". De opruiming haalt een rij pas weg nadat de foto
 * aantoonbaar verdwenen is, dus het vaultgetal loopt vanzelf terug; het scherm
 * ververst elke vijf seconden zodat dat ook te zien is.
 */
import { useMemo, useState } from 'react'

import { num } from '../lib/format'
import { useApi, usePoll } from '../lib/useApi'
import { Tag } from './ui'

interface ModelRij {
  model_persona_id: number
  slug: string
  name: string
  creator_id: string
  active: boolean
  rounds: number
  rounds_completed: number
  item: number | null
  of_total: number | null
  queue: number
  handed_over_not_live: number
  live: number
  vault: number
  failed_24h: number
}

interface Antwoord {
  models: ModelRij[]
  totals: {
    queue: number
    handed_over_not_live: number
    live: number
    vault: number
    failed_24h: number
  }
}

type Sleutel = 'name' | 'rounds' | 'queue' | 'handed_over_not_live' | 'live'
  | 'vault' | 'failed_24h'

const KOLOMMEN: { sleutel: Sleutel; label: string; uitleg: string }[] = [
  { sleutel: 'name', label: 'Model', uitleg: 'Stage name' },
  {
    sleutel: 'rounds',
    label: 'Rounds',
    uitleg: 'Posting rounds this model has had — round 3 means two full rounds '
      + 'done and the third in progress',
  },
  {
    sleutel: 'queue',
    label: 'In queue',
    uitleg: 'Scheduled posts the bot still has to hand over to the platform',
  },
  {
    sleutel: 'handed_over_not_live',
    label: 'Not live yet',
    uitleg: 'Accepted by the platform, but their moment is still in the future '
      + '(they sit in the Scheduled tab)',
  },
  { sleutel: 'live', label: 'Live now', uitleg: 'On the page right now: handed over, moment passed, not expired' },
  {
    sleutel: 'vault',
    label: 'Vault',
    uitleg: 'Bot photos still in the vault. Goes down on its own while the '
      + 'vault cleanup runs — same rows as VAULT DELETE · LIVE',
  },
  { sleutel: 'failed_24h', label: 'Failed 24h', uitleg: 'Same rows as FAILED POSTS · 24 HOURS, counted per model' },
]

export default function ModelOverzicht() {
  const data = useApi<Antwoord>('/api/admin/posting/model-overzicht')
  // Vijf seconden: de vaultopruiming haalt er meerdere per minuut weg en Jordy
  // wil dat getal zien meebewegen.
  usePoll(5000, data.refetch)

  const [sorteer, setSorteer] = useState<Sleutel>('name')
  const [omgekeerd, setOmgekeerd] = useState(false)
  const [model, setModel] = useState('all')
  const [alleenActief, setAlleenActief] = useState(false)

  const modellen = data.data?.models ?? []
  const rijen = useMemo(() => {
    const uit = modellen.filter((m) => (
      (model === 'all' || String(m.model_persona_id) === model)
      && (!alleenActief || m.active)
    ))
    uit.sort((a, b) => {
      if (sorteer === 'name') return a.name.localeCompare(b.name)
      return Number(b[sorteer]) - Number(a[sorteer])
    })
    return omgekeerd ? uit.reverse() : uit
  }, [modellen, model, alleenActief, sorteer, omgekeerd])

  const t = data.data?.totals
  const klik = (sleutel: Sleutel) => {
    if (sleutel === sorteer) setOmgekeerd(!omgekeerd)
    else { setSorteer(sleutel); setOmgekeerd(false) }
  }

  return (
    <div className="card monitorpane" style={{ marginTop: 16 }}>
      <div className="between" style={{ marginBottom: 10 }}>
        <b style={{ fontSize: 12 }}>MODELS · OVERVIEW</b>
        <div className="row" style={{ gap: 8 }}>
          <select className="inp sm" value={model} onChange={(e) => setModel(e.target.value)}>
            <option value="all">All models</option>
            {modellen.map((m) => (
              <option key={m.model_persona_id} value={String(m.model_persona_id)}>
                {m.name}
              </option>
            ))}
          </select>
          <label className="row mut2" style={{ gap: 5, fontSize: 11 }}>
            <input type="checkbox" checked={alleenActief}
                   onChange={(e) => setAlleenActief(e.target.checked)} />
            active only
          </label>
          {t ? (
            <Tag kind={t.failed_24h ? 'bad' : 'acc'}>
              {num(t.live)} live · {num(t.vault)} vault · {num(t.failed_24h)} failed
            </Tag>
          ) : null}
        </div>
      </div>
      <div className="panescroll" style={{ maxHeight: 420 }}>
        {!rijen.length ? (
          <div className="empty" style={{ padding: 18 }}>
            {data.data ? 'No models' : 'Loading…'}
          </div>
        ) : (
          <div className="tblwrap">
          <table className="tbl" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                {KOLOMMEN.map((k) => (
                  <th key={k.sleutel} title={k.uitleg}
                      style={{
                        textAlign: k.sleutel === 'name' ? 'left' : 'right',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                      onClick={() => klik(k.sleutel)}>
                    {k.label}
                    {sorteer === k.sleutel ? (omgekeerd ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rijen.map((m) => (
                <tr key={m.model_persona_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {m.name}
                    {!m.active ? (
                      <span className="mut2" style={{ fontSize: 11 }}> · paused</span>
                    ) : null}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {num(m.rounds)}
                    {m.item && m.of_total ? (
                      <span className="mut2" style={{ fontSize: 11 }}>
                        {` · item ${m.item}/${m.of_total}`}
                      </span>
                    ) : null}
                  </td>
                  <td style={{ textAlign: 'right' }}>{num(m.queue)}</td>
                  <td style={{ textAlign: 'right' }}>{num(m.handed_over_not_live)}</td>
                  <td style={{ textAlign: 'right' }}>{num(m.live)}</td>
                  <td style={{ textAlign: 'right' }}>{num(m.vault)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {m.failed_24h ? <Tag kind="bad">{num(m.failed_24h)}</Tag> : num(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <div className="note" style={{ fontSize: 11, marginTop: 8 }}>
        Vault counts the bot photos still tracked for cleanup — the same rows as
        VAULT DELETE · LIVE, refreshed every 5 s, so it drops while the cleanup
        runs. Failed uses the same rules as FAILED POSTS · 24 HOURS.
      </div>
    </div>
  )
}
